import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { notification } from 'antd'
import { AccountContext } from '@/contexts/account'
import type { AccountContextValue } from '@/contexts/account'
import { LoaderContextCreator } from '@/contexts/loader'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { canExportCsv, ensureCsvExportPermissionOrAlert, shouldBypassHotelPmsPermissionUi } from '@/libs/hotel-pms-permission'

/**
 * 売上管理の出力を依頼する。出力はバッチ用のサーバーで行うため、
 * 依頼できたら出力履歴からダウンロードするよう案内する（売上明細・締め・会計の各画面で共通）
 */
export const useSalesExportRequest = () => {
  const { t } = useTranslation()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { errorHandler } = useErrorHandler()
  const { permissions, permissionsLoaded, permissionsError } = useContext<AccountContextValue>(AccountContext)

  const requestExport = async (request: () => Promise<unknown>) => {
    // ほかの CSV 出力と同じく、権限が無ければ依頼する前に伝える
    if (!ensureCsvExportPermissionOrAlert(permissions, t, 'sales_csv_export', { permissionsLoaded, permissionsError })) {
      return
    }
    try {
      setIsLoading(true)
      await request()
      notification.open({ message: t('SalesExport.Requested') })
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  return { requestExport }
}

/**
 * 出力履歴の権限。出力履歴の API は売上の CSV 出力権限を求める。
 * - canView: タブを出すか。権限の読み込み中はサイドメニューと同じく出す側に倒す（読み込み後に権限が無ければ消える）
 * - canFetch: 一覧を取得してよいか。読み込み中に取得すると権限の無い人に 403 のアラートが出るため、読み込みを待つ
 * - permissionsError: 権限を読み込めなかったか（権限が無いのとは別の案内を出す）
 */
export const useSalesExportHistoryPermission = (): { canView: boolean; canFetch: boolean; permissionsError: boolean } => {
  const { permissions, permissionsLoaded, permissionsError } = useContext<AccountContextValue>(AccountContext)
  if (shouldBypassHotelPmsPermissionUi()) {
    return { canView: true, canFetch: true, permissionsError: false }
  }
  const permissionsPending = !permissionsLoaded && !permissionsError
  return {
    canView: canExportCsv(permissions, 'sales_csv_export', permissionsPending ? 'allow' : 'deny'),
    canFetch: canExportCsv(permissions, 'sales_csv_export', 'deny'),
    permissionsError: Boolean(permissionsError),
  }
}
